import { useState, useCallback, useRef } from "react";
import { fetchPost } from "../utils/axios.tsx";

type TTSState = "idle" | "loading" | "playing";

export function useTextToSpeech() {
  const [state, setState] = useState<TTSState>("idle");
  const mediaSourceRef = useRef<MediaSource | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const sourceBufferRef = useRef<SourceBuffer | null>(null);
  const playAttemptedRef = useRef(false);
  const isStoppedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    if (isStoppedRef.current) return;

    isStoppedRef.current = true;

    // Abort any ongoing fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Clean up audio
    if (audioRef.current) {
      const audio = audioRef.current;

      // Remove event listeners
      audio.onended = null;
      audio.onerror = null;

      // Pause and clean up
      audio.pause();

      // Revoke object URL if it exists
      if (audio.src.startsWith("blob:")) {
        URL.revokeObjectURL(audio.src);
      }

      audio.src = "";
      audioRef.current = null;
    }

    // Clean up MediaSource
    if (mediaSourceRef.current) {
      const mediaSource = mediaSourceRef.current;
      try {
        if (mediaSource.readyState !== "closed") {
          mediaSource.endOfStream();
        }
      } catch (err) {
        // Ignore errors during cleanup
      }

      mediaSourceRef.current = null;
    }

    // Clean up SourceBuffer
    sourceBufferRef.current = null;

    // Reset flags
    playAttemptedRef.current = false;
    isStoppedRef.current = false;

    // Update state
    setState("idle");
  }, []);

  const attemptPlay = useCallback(async (audio: HTMLAudioElement) => {
    if (playAttemptedRef.current || isStoppedRef.current) return;

    playAttemptedRef.current = true;

    try {
      await audio.play();
      setState("playing");
      return true;
    } catch (err) {
      console.error("Audio play failed:", err);
      return false;
    }
  }, []);

  const speak = useCallback(
    async (exchangeID: string | undefined, text: string) => {
      // Stop any current playback
      stop();

      // Reset stop flag
      isStoppedRef.current = false;
      abortControllerRef.current = new AbortController();

      setState("loading");

      return new Promise<void>(async (resolve, reject) => {
        try {
          // Create MediaSource
          const mediaSource = new MediaSource();
          mediaSourceRef.current = mediaSource;

          // Create audio element
          const audio = new Audio();
          audioRef.current = audio;
          audio.volume = 1.0;

          // Create object URL
          const objectUrl = URL.createObjectURL(mediaSource);
          audio.src = objectUrl;

          const onSourceOpen = async () => {
            if (isStoppedRef.current) {
              URL.revokeObjectURL(objectUrl);
              return;
            }

            try {
              // Create SourceBuffer
              const mimeType = "audio/mpeg";
              if (!MediaSource.isTypeSupported(mimeType)) {
                throw new Error(`MIME type ${mimeType} not supported`);
              }

              const sourceBuffer = mediaSource.addSourceBuffer(mimeType);
              sourceBufferRef.current = sourceBuffer;

              // Set up audio events
              audio.onended = () => {
                if (!isStoppedRef.current) {
                  stop();
                  resolve();
                }
              };

              audio.onerror = () => {
                // Ignore empty src errors (code 4) which are expected during cleanup
                if (audio.error?.code === 4) {
                  return;
                }

                console.error("Audio element error:", audio.error);
                if (!isStoppedRef.current) {
                  stop();
                  reject(new Error(`Audio error: ${audio.error?.message}`));
                }
              };

              // Set up SourceBuffer events
              sourceBuffer.onerror = () => {
                if (!isStoppedRef.current) {
                  stop();
                  reject(new Error("SourceBuffer error"));
                }
              };

              // Fetch audio stream
              const response = await fetchPost(
                "/v1/text-to-speech/",
                {
                  text,
                  exchange_id: exchangeID,
                },
                {
                  signal: abortControllerRef.current?.signal,
                },
              );

              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }

              if (!response.body) {
                throw new Error("Response has no body");
              }

              const reader = response.body.getReader();
              let bufferQueue: Uint8Array[] = [];
              let isProcessing = true;
              let totalBytes = 0;

              // Function to safely append buffer
              const safeAppendBuffer = (chunk: Uint8Array) => {
                if (
                  isStoppedRef.current ||
                  !sourceBuffer ||
                  sourceBuffer.updating
                ) {
                  return false;
                }

                try {
                  sourceBuffer.appendBuffer(chunk);
                  return true;
                } catch (err) {
                  // Ignore errors if we're stopping
                  if (!isStoppedRef.current) {
                    console.error("Failed to append buffer:", err);
                  }
                  return false;
                }
              };

              // Function to process queue
              const processQueue = () => {
                if (
                  isStoppedRef.current ||
                  !sourceBuffer ||
                  bufferQueue.length === 0
                ) {
                  return;
                }

                if (!sourceBuffer.updating) {
                  const chunk = bufferQueue.shift()!;
                  const appended = safeAppendBuffer(chunk);

                  if (!appended) {
                    bufferQueue.unshift(chunk);
                  }
                }
              };

              // Process stream
              const processStream = async () => {
                try {
                  while (isProcessing && !isStoppedRef.current) {
                    const { done, value } = await reader.read();

                    if (done || isStoppedRef.current) {
                      isProcessing = false;

                      // End stream if MediaSource is still open
                      if (
                        !isStoppedRef.current &&
                        mediaSource.readyState === "open"
                      ) {
                        try {
                          mediaSource.endOfStream();
                        } catch (err) {
                          // Ignore errors during cleanup
                        }
                      }

                      if (!isStoppedRef.current) {
                        resolve();
                      }

                      break;
                    }

                    if (value && !isStoppedRef.current) {
                      totalBytes += value.byteLength;

                      // Add to queue
                      bufferQueue.push(value);

                      // Process queue
                      processQueue();

                      // Try to play after we have enough data
                      if (
                        !playAttemptedRef.current &&
                        totalBytes > 50000 &&
                        !isStoppedRef.current
                      ) {
                        setTimeout(() => {
                          if (!isStoppedRef.current) {
                            attemptPlay(audio);
                          }
                        }, 300);
                      }
                    }
                  }
                } catch (err) {
                  // Check if it's an AbortError
                  if (err instanceof Error && err.name === "AbortError") {
                    // Stream was aborted intentionally
                    return;
                  }

                  console.error("Error processing stream:", err);
                  if (!isStoppedRef.current) {
                    stop();
                    reject(err);
                  }
                }
              };

              // Handle sourceBuffer update events
              sourceBuffer.onupdateend = () => {
                if (isStoppedRef.current) return;

                // Process next item in queue
                processQueue();

                // If queue is empty and we have data, try to play
                if (
                  bufferQueue.length === 0 &&
                  !playAttemptedRef.current &&
                  totalBytes > 10000
                ) {
                  setTimeout(() => {
                    if (!isStoppedRef.current) {
                      attemptPlay(audio);
                    }
                  }, 100);
                }
              };

              // Start processing the stream
              processStream();

              // Safety timeout to attempt play
              setTimeout(() => {
                if (
                  !isStoppedRef.current &&
                  state === "loading" &&
                  !playAttemptedRef.current
                ) {
                  attemptPlay(audio);
                }
              }, 2000);
            } catch (err) {
              console.error("Error in sourceopen handler:", err);
              if (!isStoppedRef.current) {
                stop();
                reject(err);
              }
            }
          };

          const onSourceError = () => {
            if (!isStoppedRef.current) {
              stop();
              reject(new Error("MediaSource error"));
            }
          };

          mediaSource.addEventListener("sourceopen", onSourceOpen);
          mediaSource.addEventListener("error", onSourceError);

          // Cleanup function for this speak call
          const cleanup = () => {
            mediaSource.removeEventListener("sourceopen", onSourceOpen);
            mediaSource.removeEventListener("error", onSourceError);
            URL.revokeObjectURL(objectUrl);
          };

          // Auto-cleanup after completion or error
          const originalResolve = resolve;
          const originalReject = reject;

          resolve = () => {
            cleanup();
            originalResolve();
          };

          reject = (err: any) => {
            cleanup();
            originalReject(err);
          };
        } catch (err) {
          console.error("Error in speak function:", err);
          if (!isStoppedRef.current) {
            stop();
            reject(err);
          }
        }
      });
    },
    [stop, state, attemptPlay],
  );

  return {
    speak,
    stop,
    state,
    isPlaying: state === "playing",
    isLoading: state === "loading",
  };
}
